import { cls } from './cls'

/*
 * A note on conditional styling in this file's callers: never put a base colour
 * utility and a conditional one for the SAME property on one element. Tailwind
 * resolves conflicts by CSS source order, not by the order the classes appear in
 * the string, so `'bg-transparent …', active && 'bg-ink-1000 text-white'` produced
 * white text on a transparent background — invisible, measured at 1.06:1 — because
 * `bg-transparent` won and `text-white` won. Use a ternary so exactly one of the
 * two is ever emitted.
 */

/**
 * Classes for a pill toggle — a selected/unselected chip.
 *
 * Emits the selected OR the unselected colours, never both. Passing a base class
 * with `bg-transparent text-ink-500` and then conditionally adding
 * `bg-ink-1000 text-white` puts two competing utilities on one element, and
 * Tailwind resolves that by CSS source order rather than class order. In practice
 * `bg-transparent` won and `text-white` won, so every selected chip rendered white
 * text on the page background — invisible, measured at 1.06:1.
 */
export const pillClass = (selected: boolean, extra = ''): string =>
  cls(
    'px-3 py-1 text-xs rounded-full cursor-pointer whitespace-nowrap transition-colors border',
    selected
      ? 'bg-ink-1000 border-ink-1000 text-white font-semibold'
      : 'bg-transparent border-transparent text-ink-500 font-medium hover:text-ink-1000 hover:bg-ink-50',
    extra,
  )

/**
 * Near-black or near-white, whichever is readable on `bg`.
 *
 * The dashboard's stage bar picks each segment's background from a palette in JS
 * but had its text colour pinned to `--paper` in CSS. That reads well on the dark
 * stages and not at all on the light ones — the Booked segment measured 1.5:1 and
 * New 1.93:1, so the counts a manager reads every day were effectively blank.
 * Deriving the ink from the background fixes every stage at once, including any
 * colour added to the palette later.
 *
 * Accepts `#rgb`, `#rrggbb` or `rgb()/rgba()`. Anything unparseable falls back to
 * dark ink, which is readable on the light surfaces this app uses.
 */
export function readableInkOn(bg: string): string {
  const hex = bg.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)
  let rgb: number[] | null = null
  if (hex) {
    const h = hex[1].length === 3 ? [...hex[1]].map(c => c + c).join('') : hex[1]
    rgb = [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16))
  } else {
    const m = bg.match(/[\d.]+/g)
    if (m && m.length >= 3) rgb = m.slice(0, 3).map(Number)
  }
  if (!rgb) return 'var(--color-ink-1000)'
  const lin = (v: number) => {
    const c = v / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }
  const L = 0.2126 * lin(rgb[0]) + 0.7152 * lin(rgb[1]) + 0.0722 * lin(rgb[2])
  // 0.45 rather than 0.5: white-on-mid-tone loses legibility before black does,
  // so the switch to dark ink happens slightly earlier.
  return L > 0.45 ? 'var(--color-ink-1000)' : 'var(--color-paper)'
}
